import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomNavigation } from './NavigationContext';
import styles from '../styles/components/_FoodTeaser.module.scss';
import { fetchFood } from '../api/api';
import FoodModalContent from './FoodModalContent';
import Modal from './Modal';

const FoodTeaser = () => {
  const { customNavigate } = useCustomNavigation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [touchEnd, setTouchEnd] = useState({ x: 0, y: 0 });
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [slidesPerView, setSlidesPerView] = useState(3);
  const carouselRef = useRef(null);
  const minSwipeDistance = 50;

  const { data: foodsData = [], isLoading } = useQuery({
    queryKey: ['foods'],
    queryFn: fetchFood,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSlidesPerView(1);
      } else {
        setSlidesPerView(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, foodsData.length - slidesPerView);

  const getTranslateX = useCallback(() => {
    const baseTranslation = (currentIndex * 100) / slidesPerView;
    return `translateX(-${baseTranslation}%)`;
  }, [currentIndex, slidesPerView]);

  const nextSlide = useCallback(() => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) =>
        prevIndex >= maxIndex ? 0 : prevIndex + 1
      );
      setTimeout(() => setIsAnimating(false), 500);
    }
  }, [maxIndex, isAnimating]);

  const previousSlide = useCallback(() => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) =>
        prevIndex === 0 ? maxIndex : prevIndex - 1
      );
      setTimeout(() => setIsAnimating(false), 500);
    }
  }, [maxIndex, isAnimating]);

  useEffect(() => {
    if (foodsData.length > 0 && !isPaused && !isModalOpen) {
      const timer = setInterval(nextSlide, 5000);
      return () => clearInterval(timer);
    }
  }, [foodsData, isPaused, nextSlide, isModalOpen]);

  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  const handleIndicatorClick = (index) => {
    if (!isAnimating && index !== currentIndex) {
      setIsAnimating(true);
      setCurrentIndex(index);
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  const handleTouchStart = (e) => {
    setTouchEnd({ x: 0, y: 0 });
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
    setIsPaused(true);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
  };

  const handleTouchEnd = () => {
    setIsPaused(false);
    if (!touchStart.x || !touchEnd.x) return;

    const horizontalDistance = touchStart.x - touchEnd.x;
    const verticalDistance = Math.abs(touchStart.y - touchEnd.y);

    const minSwipeDistance = 30;

    if (Math.abs(horizontalDistance) > verticalDistance && Math.abs(horizontalDistance) > minSwipeDistance) {
      if (horizontalDistance > 0) {
        nextSlide();
      } else {
        previousSlide();
      }
    }
  };

  const handleOpenMenu = (food) => {
    setIsPaused(true);
    setSelectedFood(food);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFood(null);
    setIsPaused(false);
  };

  if (isLoading || foodsData.length === 0) {
    return null;
  }

  return (
    <>
      <h2 className={styles.title}>Food & Drinks</h2>
      <div 
        className={styles.foodTeaser}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: 'pan-y pinch-zoom' }}
      >
        <div className={styles.carouselContainer}>
          <div
            className={styles.carouselTrack}
            style={{ 
              transform: getTranslateX()
            }}
          >
            {foodsData.map((food, index) => (
              <div
                key={food.id}
                className={`${styles.slide} ${
                  index >= currentIndex && index < currentIndex + slidesPerView ? styles.active : ""
                }`}
                style={{
                  backgroundImage: `url(${food.acf?.image?.url})`,
                }}
              >
                <div className={styles.slideInner}>
                  <div className={styles.overlay}>
                    <div
                      className={`${styles.content} ${
                        index >= currentIndex && index < currentIndex + slidesPerView ? styles.active : ""
                      }`}
                    >
                      <div
                        className={`${styles.logoContainer} ${
                          index >= currentIndex && index < currentIndex + slidesPerView ? styles.active : ""
                        }`}
                      >
                        <img
                          src={food.acf?.logo?.url}
                          alt={food.title?.rendered || ''}
                          className={styles.logoImage}
                        />
                      </div>
                      <h3 className={styles.foodTruckName}>{food.title?.rendered}</h3>
                      <div className={styles.actionButtons}>
                        <button 
                          className={styles.menuButton}
                          onClick={() => handleOpenMenu(food)}
                        >
                          Voir le menu
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.indicators}>
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <button
              key={index}
              className={`${styles.indicator} ${
                index === currentIndex ? styles.active : ""
              }`}
              onClick={() => handleIndicatorClick(index)}
            />
          ))}
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        <FoodModalContent food={selectedFood} onClose={handleCloseModal} />
      </Modal>
    </>
  );
};

export default FoodTeaser;